/** "About this analysis" intros (UX spec §3.3, finding B1): one per analysis
 *  surface, rendered by components/engineering/AboutAnalysis.tsx as a
 *  collapsible block. Five prompts each: what question the analysis answers,
 *  what data goes in, how it works, where it is weak, and what an operator
 *  should do with the result. Register: plain distribution-ops English —
 *  no script paths, no spec references, no snake_case (spec §1). */

export interface AnalysisIntro {
  key: string; title: string; question: string; inputs: string;
  method: string; limits: string; action: string;
}

export const ANALYSIS_INTROS: Record<string, AnalysisIntro> = {
  capacity: {
    key: 'capacity',
    title: 'Transformer capacity',
    question: 'Which transformers are loaded near, at, or past their nameplate rating?',
    inputs: 'Transformer kVA ratings and meter links from the system model, AMI measured demand where meters report, and a flat per-meter estimate where they do not.',
    method: 'Each transformer’s peak demand over the window — the measured coincident peak where AMI coverage is good (plus a per-silent-meter estimate), the model estimate otherwise — is divided by its kVA rating.',
    limits: 'Only as good as the kVA ratings and meter-to-transformer links on record; silent meters are estimated, not measured.',
    action: 'Work the overloaded and heavy rows first. A number that looks impossible usually means a wrong kVA rating or wrong meter links — flag it for GIS cleanup rather than trusting it.',
  },
  'voltage-drop': {
    key: 'voltage-drop',
    title: 'Voltage drop',
    question: 'Are customers at the far end of this feeder getting acceptable voltage?',
    inputs: 'Conductor lengths and impedances from the system model, with transformer loads placed along the feeder.',
    method: 'A simplified calculation accumulates voltage drop outward from the substation; more than 5% drop is flagged as a violation, 3–5% as a caution.',
    limits: 'An approximation, not a full power-flow solve. Segments using default impedance weaken it, and rows showing 0.0 pu are disconnected model data — not real voltages.',
    action: 'Treat violations as places to investigate: check connectivity and conductor data first, and cross-check with the OpenDSS run before planning any fix.',
  },
  'phase-balance': {
    key: 'phase-balance',
    title: 'Phase balance',
    question: 'Is this feeder’s load spread evenly across phases A, B, and C?',
    inputs: 'Phase assignments and connected load for each transformer on the feeder.',
    method: 'Adds up the load served by each phase and compares the shares; a lopsided split is flagged as imbalance.',
    limits: 'Phase tags in the source data are often missing or wrong, and single-phase transformer assignments drive the whole result.',
    action: 'A big imbalance makes this feeder a re-phasing candidate — but field-verify the phase tags before acting on it.',
  },
  fault: {
    key: 'fault',
    title: 'Fault study',
    question: 'How much current would flow for a short circuit at each point on this feeder?',
    inputs: 'Substation source strength and conductor impedances from the system model.',
    method: 'Builds the feeder’s impedance network and computes bolted three-phase and single line-to-ground fault currents at every node.',
    limits: 'While the study is marked indicative, the source data is a placeholder and the magnitudes are not real. Default impedances blur results, and motor contribution is not included.',
    action: 'Judge severity through the Device Duty card below — fault current against each device’s rating — not by eyeballing raw kA numbers.',
  },
  duty: {
    key: 'duty',
    title: 'Device duty',
    question: 'Can each fuse, recloser, and breaker safely interrupt the fault current available where it sits?',
    inputs: 'Fault-study currents at each device location plus each device’s interrupting rating.',
    method: 'Divides available fault current by the rating: 80–100% is marginal, over 100% inadequate. Fuses are judged on the larger of the three-phase and line-to-ground currents; reclosers on asymmetrical current.',
    limits: 'Unknown means the rating is missing — not that the device passed. Verdicts inherit the fault study’s indicative status.',
    action: 'Inadequate devices are a safety finding — raise them with the engineer. Unknowns are a data-collection worklist: get nameplate ratings recorded.',
  },
  'arc-flash': {
    key: 'arc-flash',
    title: 'Arc flash screening',
    question: 'Roughly how severe would an arc flash be at each device, and what protection level would work require?',
    inputs: 'Fault currents, device clearing times (real curve data where on file, estimates otherwise), and working distance and enclosure type inferred from the equipment type.',
    method: 'A screening-level calculation estimates the arcing current and the heat delivered at working distance, then maps that energy to a protective-clothing category.',
    limits: 'Screening only — not a compliant arc-flash study. Estimated clearing times and inferred distances can shift a location by whole categories.',
    action: 'Use it to spot high-hazard locations worth a proper engineering study. Never use it to pick protective equipment for a live job.',
  },
  coordination: {
    key: 'coordination',
    title: 'Protection coordination',
    question: 'When a fault hits, does the device nearest the fault trip first — or does an upstream device black out more customers than necessary?',
    inputs: 'The protection hierarchy (which device backs up which) and operating-curve data for each device where on file.',
    method: 'Each upstream/downstream pair is checked for enough separation between their operating curves across the fault currents they share.',
    limits: 'Pairs without curve data cannot be assessed and are listed separately — unassessed is not coordinated. Verdicts inherit the fault study’s indicative status.',
    action: 'Flagged pairs cause oversized outages; queue them for the engineer to review settings or fuse sizes.',
  },
  ami: {
    key: 'ami',
    title: 'AMI measured',
    question: 'What are the meters actually measuring — demand and voltage — compared with what the model assumes?',
    inputs: 'Interval demand and voltage readings from the nightly meter-data load, tied to transformers through the system model.',
    method: 'Aggregates meter readings into per-transformer coincident peaks and per-meter voltage-band statistics over the window.',
    limits: 'Silent and unmatched meters are invisible here, and hybrid rows add a flat estimate for each silent meter. If the data is stale (see the as-of line), so is everything below it.',
    action: 'When measured and modeled disagree, trust measured — then find out why the model was wrong: kVA rating, meter links, or a dead meter.',
  },
  'data-quality': {
    key: 'data-quality',
    title: 'Data quality',
    question: 'How much of this feeder’s model rests on solid data versus defaults and guesses?',
    inputs: 'Where each meter and transformer relationship came from, impedance coverage, model connectivity, device rating coverage, and verification counts.',
    method: 'Rolls the counts up per category so the weakest inputs are visible before you lean on any analysis tab.',
    limits: 'It reports what is recorded — a verified flag is only as trustworthy as whoever set it.',
    action: 'Use it to calibrate how much to trust the other tabs, and as the running worklist for GIS cleanup.',
  },
  'what-if': {
    key: 'what-if',
    title: 'What-if (new load)',
    question: 'What happens to a transformer if a new load — a shop, an EV charger, an addition — is connected?',
    inputs: 'Current loading from this dashboard plus the size and location of the load you enter.',
    method: 'Re-runs the loading calculation with the extra demand added and shows the new loading percentage and status.',
    limits: 'A preview on the same assumptions as the capacity tab — it inherits any wrong rating or wrong meter links.',
    action: 'Good for a first read on a service request; send borderline results to the engineer.',
  },
  opendss: {
    key: 'opendss',
    title: 'OpenDSS power flow',
    question: 'What does a full power-flow engine say about this feeder’s voltages and loading?',
    inputs: 'The feeder model — conductors, transformers, loads — exported to the OpenDSS engine with the same average-demand assumption as the other tabs.',
    method: 'OpenDSS solves the complete unbalanced power flow and reports voltages and flows for every element.',
    limits: 'Same input data as everything else — a clean solve does not bless bad model data, and a failure to converge usually means a model defect.',
    action: 'Use it as the check on the simplified voltage-drop numbers; a big disagreement is a model problem worth flagging.',
  },
  'device-ratings': {
    key: 'device-ratings',
    title: 'Device rating coverage',
    question: 'How many protective devices on this feeder have a usable interrupting rating on record?',
    inputs: 'The protective-device list for the feeder and its rating fields.',
    method: 'Counts devices whose rating is recorded, readable from nameplate text, missing entirely, or not placeable on the feeder.',
    limits: 'Measures coverage, not correctness — a recorded rating can still be wrong.',
    action: 'Missing ratings block the duty evaluation. Collect nameplate data in the field and get it entered.',
  },
  'fault-compare': {
    key: 'fault-compare',
    title: 'Fault study compare',
    question: 'Has the fault picture changed since the last saved snapshot?',
    inputs: 'The most recent saved fault-study snapshot and a fresh run of the same study.',
    method: 'Runs the study live and shows per-node differences against the snapshot.',
    limits: 'Differences reflect model edits as much as real system change; with no snapshot saved there is nothing to compare.',
    action: 'Review large deltas after model edits, and save a new snapshot once they are understood.',
  },
}
